import type {
	FetchArgs,
	FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { env } from 'next-runtime-env';

import { TokenManager } from '~shared/services/ClientTokenManager';

import { apiTags } from './names';

const hasCustomAuthHeader = (args: string | FetchArgs): boolean => {
	if (typeof args !== 'object') return false;
	const headers = args?.headers;

	return (
		typeof headers === 'object' &&
		!!(headers as Record<string, string>)?.['authorization']
	);
};

const retryBaseWithAuth = retry(
	async (args: string | FetchArgs, api, extraOptions) => {
		const result = await fetchBaseQuery({
			baseUrl: env('NEXT_PUBLIC_PUBLIC_API') ?? 'https://api.ifriend.ai',
			prepareHeaders: (headers) => {
				if (typeof window === 'undefined') {
					return headers;
				}

				headers.set('Accept-Language', document.documentElement.lang);
				const { token } = TokenManager.getInfo();
				//hasCustomAuthHeader checks if we pass another header manually (e.g. login)
				if (token && !hasCustomAuthHeader(args)) {
					headers.set('authorization', `Token token="${token}"`);
				}
				return headers;
			},
		})(args, api, extraOptions);

		// cancel retries immediately if unauthorized,
		if ([401, 403].includes(<number>result.error?.status ?? 0)) {
			if (result.error?.status === 401) {
				const { token, type } = TokenManager.getInfo();
				if (token) {
					TokenManager.clearToken(type === 'demo' ? 'demo' : 'main');
				}
			}
			retry.fail(result.error);
		}

		return result;
	},
	{
		backoff: (attempt) => {
			// 600 ms, 1200 ms
			// maxRetries doesn't actually work when retry condition function defined
			const baseTimeout = 600;
			const incrementTimeout = baseTimeout * attempt;

			const timeout = incrementTimeout + baseTimeout;
			return new Promise((resolve) => {
				setTimeout(resolve, timeout);
			});
		},
		retryCondition: (
			err: FetchBaseQueryError,
			_: unknown,
			extraArgs: { attempt: number; baseQueryApi: { endpoint: string } }
		) => {
			if (
				extraArgs.attempt > 2 ||
				extraArgs?.baseQueryApi?.endpoint === 'authenticate' ||
				(extraArgs?.baseQueryApi?.endpoint === 'sendMessage' &&
					err.status === 429)
			) {
				return false;
			}
			if (typeof err.status === 'string') {
				return false;
			}
			return err.status === 429 || err.status > 501 || err.status === 500;
		},
	}
);

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
	reducerPath: 'api',
	/**
	 * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
	 */
	baseQuery: retryBaseWithAuth,
	/**
	 * Tag types must be defined in the original API definition
	 * for any tags that would be provided by injected endpoints
	 */
	tagTypes: [
		apiTags.gifts,
		apiTags.subscriptions,
		apiTags.shop,
		apiTags.contacts,
		apiTags.messages,
		apiTags.imagesGenerationBots,
		apiTags.imagesGenerationQuota,
		apiTags.characterGenerationQuota,
		apiTags.generatedMedia,
		apiTags.generatedPublicMedia,
		apiTags.generatedMediaById,
		apiTags.generatedSimilarMedia,
		apiTags.generatedLikedMedia,
		apiTags.generatedUnlockedMedia,
		apiTags.grid,
		apiTags.tags,
		apiTags.userProps,
		apiTags.userConfig,
		apiTags.videosGenerationBots,
		apiTags.videosGenerationQuota,
		apiTags.liveops,
		apiTags.botGallery,
		apiTags.chats,
		apiTags.nextBots,
		apiTags.suggestedTexts,
	],
	/**
	 * This api has endpoints injected in adjacent files,
	 * which is why no endpoints are shown below.
	 * If you want all endpoints defined in the same file, they could be included here instead
	 */
	endpoints: () => ({}),
});
